import { useIntl } from "react-intl"
import { useDispatch } from "react-redux"
import { useState } from "react"
import { Combobox, EInputTypes, ETypeOfNotification, InputDate, InputTextCustom, TextArea, WindowLoader, formatDate, getDate, setNotification, useFormValidation } from "medere-ui-framework"
import { BudgetDTO } from "../../dto/budget/BudgetDTO"
import { ComboDataDTO } from "../../dto/ComboDataDTO"
import { Button } from "primereact/button"
import './Budget.scss'
import CancelBudgetModal from "./CancelBudgetModal"
import BudgetTable from "./tables/BudgetTable"
import { HealthEntityPlanDTO } from "../../dto/HealthEntityPlanDTO"
import { saveBudget } from "../../servicesHooks/Budget/useBudgetService"
import { HttpStatusCode } from "axios"
import { useEntityService } from "../../servicesHooks/Entity/useEntityService"

/**
 * Campos obligatorios al guardar: los del formulario que se marcan en rojo. Se valida solo esto porque el resto del
 * DTO (surgery, budgetnumber, total…) puede venir en null del backend, y eso bloqueaba el guardado sin marcar nada.
 */
const REQUIRED_FIELDS: (keyof BudgetDTO)[] = ["date", "addressee", "duration", "agreement", "agreementplan", "detail", "budgetvalidity", "paymentvalidity"];

interface CRUBudgetViewProps {
    budget: BudgetDTO;
    hideView: () => void;
}

export default function CRUBudgetView({ budget, hideView }: CRUBudgetViewProps) {

    const { getHealthEntityComboDataDTO, getHealthEntityPlanDTO } = useEntityService();
    const intl = useIntl();
    const dispatch = useDispatch();
    const [CRUBudget, setCRUBudget] = useState<BudgetDTO>(budget);
    const [defaultValuesBudget] = useState<BudgetDTO>(budget);
    const [openCancelBudgetModal, setOpenCancelBudgetModal] = useState<boolean>(false);
    const [generalEarnings, setGeneralEarnings] = useState<number>(0);
    const { errors: formErrors, handleValidation } = useFormValidation(CRUBudget);
    const [shouldUpdateEquipmentPrices, setShouldUpdateEquipmentPrices] = useState(false);
    const [loading, setLoading] = useState(false);

    function cancel(value: boolean) {
        setOpenCancelBudgetModal(false);
        if (value) hideView();
    }

    function save(budgetToSave: BudgetDTO, withValidation: boolean) {
        if (withValidation) {
            const ignoredFields = (Object.keys(budgetToSave) as (keyof BudgetDTO)[]).filter(field => !REQUIRED_FIELDS.includes(field));
            handleValidation(
                () => save(budgetToSave, false),
                // Se valida desde cero: un error viejo de un campo que ya no se pide no tiene que bloquear.
                {},
                ignoredFields,
                () => dispatch(setNotification({ title: "No pudimos guardar los datos", desc: "Te indicamos con color los campos a repasar", icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }))
            );
            return;
        }
        setLoading(true);
        saveBudget(budgetToSave)?.then(res => {
            if (res.status !== HttpStatusCode.Ok) {
                dispatch(setNotification({ title: "No pudimos guardar los datos", desc: res.statusText, icon: 'pi-times', typeOfNotification: ETypeOfNotification.ERROR }));
                return;
            }
            dispatch(setNotification({ title: "Datos guardados", icon: 'pi-check-circle', typeOfNotification: ETypeOfNotification.INFO }));
            hideView();
        }).finally(() => setLoading(false));
    }

    return (
        <div className="h-full">
            <div className="budget-form-header">
                <div className='flex flex-col md:flex-row justify-center budget-form-row'>
                    <InputDate label={"Fecha presupuesto"}
                        className="input-common md:w-3/12"
                        value={getDate(CRUBudget.date)}
                        defaultValue={getDate(defaultValuesBudget?.date)}
                        error={formErrors.date}
                        onChange={(e: any) => setCRUBudget({ ...CRUBudget, date: e.value })}
                        required
                    />
                    <InputTextCustom label={intl.formatMessage({ id: "Addressee" })}
                        className="input-common md:w-4/12"
                        value={CRUBudget.addressee}
                        defaultValue={defaultValuesBudget?.addressee}
                        inputType={EInputTypes.TEXT}
                        error={formErrors.addressee}
                        onChange={(e: any) => setCRUBudget({ ...CRUBudget, addressee: e.target.value })}
                        required
                    />
                    <InputTextCustom label={"DNI"}
                        className="input-common md:w-2/12"
                        value={CRUBudget.dni}
                        defaultValue={defaultValuesBudget?.dni}
                        inputType={EInputTypes.TEXT}
                        onChange={(e: any) => setCRUBudget({ ...CRUBudget, dni: e.target.value })}
                    />
                    <InputTextCustom label={"Nº Afiliado"}
                        className="input-common md:w-3/12"
                        value={CRUBudget.affiliatenumber}
                        defaultValue={defaultValuesBudget?.affiliatenumber}
                        inputType={EInputTypes.TEXT}
                        onChange={(e: any) => setCRUBudget({ ...CRUBudget, affiliatenumber: e.target.value })}
                    />
                </div>

                <div className='flex flex-col md:flex-row justify-center budget-form-row'>
                    <InputTextCustom label={intl.formatMessage({ id: "Duration" })}
                        className="input-common md:w-1/12"
                        value={CRUBudget.duration}
                        defaultValue={defaultValuesBudget?.duration}
                        inputType={EInputTypes.NUMBER}
                        error={formErrors.duration}
                        onChange={(e: any) => setCRUBudget({ ...CRUBudget, duration: e.target.value })}
                        required
                    />
                    <Combobox
                        label={intl.formatMessage({ id: 'Agreement' })}
                        className={`input-common md:${(CRUBudget.agreement !== -1 && CRUBudget.agreementdesc !== "") ? 'w-2/12' : 'w-4/12'}`}
                        getItems={getHealthEntityComboDataDTO}
                        optionLabel="name"
                        placeholder="Seleccionar"
                        value={CRUBudget.agreementdesc}
                        defaultValue={defaultValuesBudget?.agreementdesc}
                        setValue={async (e: ComboDataDTO) => {
                            if (e) {
                                setCRUBudget(prev => ({ ...prev, agreement: e.id, agreementdesc: e.name, agreementplan: -1, agreementplandesc: '' }));
                                const plans = await getHealthEntityPlanDTO({ filterValue: '', offSet: 0, pageSize: 10, params: { healthentity: e.id } });
                                if (plans.length === 1) {
                                    setCRUBudget(prev => ({ ...prev, agreementplan: plans[0].healthentityplan, agreementplandesc: plans[0].name }));
                                } else if (plans.length > 1) {
                                    setCRUBudget(prev => ({ ...prev, agreementplan: -1, agreementplandesc: 'Seleccionar Plan' }));
                                }
                            }
                        }}
                        error={formErrors.agreement}
                        required
                    />
                    {CRUBudget.agreement !== -1 && CRUBudget.agreementdesc !== "" && (
                        <Combobox
                            label={intl.formatMessage({ id: 'Plan' })}
                            className="input-common md:w-4/12"
                            getItems={getHealthEntityPlanDTO}
                            params={{ healthentity: CRUBudget.agreement }}
                            optionLabel="name"
                            placeholder="Seleccionar"
                            value={CRUBudget.agreementplandesc || ""}
                            defaultValue={defaultValuesBudget?.agreementplandesc || ""}
                            setValue={(e: HealthEntityPlanDTO) => {
                                if (e) {
                                    setCRUBudget(prev => ({ ...prev, agreementplan: e.healthentityplan, agreementplandesc: e.name }));
                                }
                            }}
                            reLoadItemsValue={CRUBudget.agreement}
                            error={formErrors.agreementplan}
                            allowsEmptyValues={false}
                            required
                        />
                    )}
                    <InputTextCustom label={intl.formatMessage({ id: "Detail" })}
                        className="input-common md:w-6/12"
                        value={CRUBudget.detail}
                        defaultValue={defaultValuesBudget?.detail}
                        inputType={EInputTypes.TEXT}
                        error={formErrors.detail}
                        onChange={(e: any) => setCRUBudget({ ...CRUBudget, detail: e.target.value })}
                        required
                    />
                    <InputTextCustom label={"% Ganancia"}
                        className="input-common md:w-auto"
                        value={generalEarnings}
                        inputType={EInputTypes.NUMBER}
                        onChange={(e: any) => setGeneralEarnings(e.target.value)}
                    />
                </div>
            </div>

            <BudgetTable
                budget={CRUBudget}
                setBudget={setCRUBudget}
                defaultEarnings={generalEarnings}
                date={CRUBudget.date ?? undefined}
                selectedHealthEntity={CRUBudget.agreement ?? undefined}
                setShouldUpdateEquipmentPrices={setShouldUpdateEquipmentPrices}
                selectedHealthEntityPlan={CRUBudget.agreementplan ?? undefined}
            />

            <TextArea
                className="flex justify-content-center hover:border-0 ml-1 mr-0 mt-2"
                label={intl.formatMessage({ id: "Observations" })}
                autoResize
                value={CRUBudget.observations ?? ''}
                defaultValue={defaultValuesBudget?.observations}
                onChange={(e: any) => setCRUBudget({ ...CRUBudget, observations: e.target.value })}
            />

            <div className="flex flex-row gap-1 mt-5 align-items-center">
                <p style={{ minWidth: '175px' }}>{`Presupuesto válido por`}</p>
                <InputTextCustom
                    style={{ maxWidth: '40px' }}
                    value={CRUBudget.budgetvalidity}
                    defaultValue={defaultValuesBudget?.budgetvalidity}
                    inputType={EInputTypes.NUMBER}
                    inputClassName="text-center h-8"
                    placeholder="00"
                    error={formErrors.budgetvalidity}
                    onChange={(e: any) => setCRUBudget({ ...CRUBudget, budgetvalidity: e.target.value })}
                />
                <p style={{ minWidth: '190px' }}>{`días a partir del ${formatDate(getDate(CRUBudget.date))}, forma de pago `}</p>
                <InputTextCustom
                    style={{ maxWidth: '40px' }}
                    placeholder="00"
                    value={CRUBudget.paymentvalidity}
                    defaultValue={defaultValuesBudget?.paymentvalidity}
                    inputType={EInputTypes.NUMBER}
                    inputClassName="text-center h-8"
                    error={formErrors.paymentvalidity}
                    onChange={(e: any) => setCRUBudget({ ...CRUBudget, paymentvalidity: e.target.value })}
                />
                <p style={{ minWidth: '190px' }}>{`días de presentada la factura.`}</p>
            </div>

            <div className="flex flex-row w-full justify-content-end align-items-end">
                <Button className="buttonMain3" label={intl.formatMessage({ id: "Cancel" })} onClick={() => setOpenCancelBudgetModal(true)} disabled={loading} />
                <Button className="buttonMain" label={intl.formatMessage({ id: "Save" })} onClick={() => save(CRUBudget, true)} disabled={loading} />
            </div>

            <CancelBudgetModal
                openCancelBudgetModal={openCancelBudgetModal}
                setOpenCancelBudgetModal={setOpenCancelBudgetModal}
                cancelBudget={cancel}
                editMode={budget.budget != null}
            />
            <WindowLoader showLoader={loading} className='whiteSpinner' />
        </div>
    );
}
